<script>
import Papa from "papaparse";
import { fade, fly, slide } from "svelte/transition";
import { cubicOut } from "svelte/easing";
import { saveAs } from "file-saver";
import { toast } from "@zerodevx/svelte-toast";
import { v4 as uuidv4 } from "uuid";
import * as m from "@app/paraglide/messages.js";
import { TEAM_ROLES, DEFAULT_ROLE, normalizeRole, roleLabel, isValidEmail, errorMessage, saveMember, motion } from "./teamApi.js";

/** @type {{ teamId: string, users: any[], seatLimit: number|null, onclose: () => void, ondone: () => Promise<void> }} */
let { teamId, users, seatLimit, onclose, ondone } = $props();

const MAX_ROWS = 500;

let step = $state("file"), // file | map | importing | done
    fileName = $state(""),
    headers = $state([]),
    rows = $state([]),
    isDragging = $state(false),
    fileInputEl = $state(),
    emailCol = $state(""),
    nameMode = $state("full"), // full | split
    fullNameCol = $state(""),
    firstNameCol = $state(""),
    lastNameCol = $state(""),
    roleCol = $state(""),
    defaultRole = $state(DEFAULT_ROLE),
    progress = $state(0),
    results = $state([]);

const HEADER_HINTS = {
    email: ["email", "e-mail", "email address", "mail"],
    full: ["name", "full name", "fullname", "display name"],
    first: ["first name", "firstname", "first", "given name"],
    last: ["last name", "lastname", "last", "surname", "family name"],
    role: ["role", "user role", "permission"],
};

function findHeader(list, hints) {
    return list.find((h) => hints.includes(h.trim().toLowerCase())) || "";
}

function autoMap(list) {
    emailCol = findHeader(list, HEADER_HINTS.email) || list.find((h) => /mail/i.test(h)) || "";
    firstNameCol = findHeader(list, HEADER_HINTS.first);
    lastNameCol = findHeader(list, HEADER_HINTS.last);
    fullNameCol = findHeader(list, HEADER_HINTS.full);
    nameMode = !fullNameCol && (firstNameCol || lastNameCol) ? "split" : "full";
    roleCol = findHeader(list, HEADER_HINTS.role);
}

function readFile(file) {
    if (!file) return;
    if (!/\.(csv|txt)$/i.test(file.name)) {
        toast.push(m.team_manager_import_not_csv(), { classes: ["toast-danger"] });
        return;
    }
    Papa.parse(file, {
        header: true,
        skipEmptyLines: "greedy",
        transformHeader: (h) => h.trim(),
        complete: (res) => {
            const fields = (res.meta.fields || []).filter(Boolean);
            if (!fields.length || !res.data.length) {
                toast.push(m.team_manager_import_empty(), { classes: ["toast-danger"] });
                return;
            }
            if (res.data.length > MAX_ROWS) {
                toast.push(m.team_manager_import_too_many_rows({ max: MAX_ROWS }), { classes: ["toast-danger"] });
                return;
            }
            fileName = file.name;
            headers = fields;
            rows = res.data;
            autoMap(fields);
            step = "map";
        },
        error: (err) => {
            toast.push(m.team_manager_import_parse_failed({ error: err.message }), { classes: ["toast-danger"] });
        },
    });
}

function handleDrop(e) {
    e.preventDefault();
    isDragging = false;
    readFile(e.dataTransfer?.files?.[0]);
}

function downloadTemplate() {
    const csv = Papa.unparse([
        { "First name": "Jane", "Last name": "Doe", Email: "jane.doe@example.com", Role: "editor" },
        { "First name": "Sam", "Last name": "Lee", Email: "sam.lee@example.com", Role: "viewer" },
    ]);
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8" }), "team-members-template.csv");
}

function cell(row, col) {
    return col ? String(row[col] ?? "").trim() : "";
}

/* Each row with its parsed values and a status: ready | invalid | duplicate | exists | seat. */
const preview = $derived.by(() => {
    if (step === "file" || !emailCol) return [];
    const existing = new Set(users.map((u) => (u.email || "").toLowerCase()));
    const seen = new Set();
    let seatsLeft = seatLimit == null ? Infinity : Math.max(0, seatLimit - users.length);

    return rows.map((row, index) => {
        const email = cell(row, emailCol).toLowerCase();
        let firstName = "", lastName = "";
        if (nameMode === "full") {
            const full = cell(row, fullNameCol).replace(/\s+/g, " ");
            const space = full.indexOf(" ");
            firstName = space === -1 ? full : full.slice(0, space);
            lastName = space === -1 ? "" : full.slice(space + 1);
        } else {
            firstName = cell(row, firstNameCol);
            lastName = cell(row, lastNameCol);
        }
        const rawRole = cell(row, roleCol);
        const role = normalizeRole(rawRole) || defaultRole;
        const roleFallback = !!rawRole && !normalizeRole(rawRole);

        let status = "ready";
        if (!isValidEmail(email)) status = "invalid";
        else if (existing.has(email)) status = "exists";
        else if (seen.has(email)) status = "duplicate";
        else if (seatsLeft <= 0) status = "seat";

        if (isValidEmail(email)) seen.add(email);
        if (status === "ready") seatsLeft--;
        return { index: index + 2, email, firstName, lastName, role, rawRole, roleFallback, status };
    });
});

const readyRows = $derived(preview.filter((r) => r.status === "ready"));
const skippedCount = $derived(preview.length - readyRows.length);

function statusLabel(status) {
    switch (status) {
        case "invalid":
            return m.team_manager_import_status_invalid();
        case "exists":
            return m.team_manager_import_status_exists();
        case "duplicate":
            return m.team_manager_import_status_duplicate();
        case "seat":
            return m.team_manager_import_status_seat();
        default:
            return m.team_manager_import_status_ready();
    }
}

async function runImport() {
    const queue = readyRows.map((r) => ({ ...r }));
    step = "importing";
    progress = 0;
    results = [];

    for (let i = 0; i < queue.length; i++) {
        const row = queue[i];
        try {
            await saveMember(teamId, {
                id: uuidv4(),
                email: row.email,
                firstName: row.firstName,
                lastName: row.lastName,
                role: row.role,
                disable: false,
                groups: [],
            });
            results.push({ ...row, ok: true });
        } catch (err) {
            const error = errorMessage(err);
            results.push({ ...row, ok: false, error });
            // The server's seat check failed: every remaining row would fail the same way.
            if (/seat/i.test(error)) {
                queue.slice(i + 1).forEach((rest) => results.push({ ...rest, ok: false, error: m.team_manager_import_status_seat() }));
                break;
            }
        }
        progress = Math.round(((i + 1) / queue.length) * 100);
    }

    progress = 100;
    step = "done";
    await ondone();
}

const added = $derived(results.filter((r) => r.ok).length);
const failed = $derived(results.filter((r) => !r.ok));
</script>

<div class="csv-import">
    <div class="d-flex align-items-center gap-2 mb-3">
        <button type="button" class="btn btn-link btn-sm px-0 text-decoration-none" onclick={onclose} disabled={step === "importing"}>
            <i class="bi bi-arrow-left me-1"></i>{m.team_manager_import_back()}
        </button>
        <h5 class="mb-0 ms-2">{m.team_manager_import_title()}</h5>
        <ol class="import-steps ms-auto mb-0" aria-label={m.team_manager_import_title()}>
            <li class:active={step === "file"}>{m.team_manager_import_step_file()}</li>
            <li class:active={step === "map"}>{m.team_manager_import_step_map()}</li>
            <li class:active={step === "importing" || step === "done"}>{m.team_manager_import_step_import()}</li>
        </ol>
    </div>

    {#if step === "file"}
        <div
            class="drop-zone {isDragging ? 'dragging' : ''}"
            in:fade={{ duration: motion(180) }}
            role="button"
            tabindex="0"
            aria-label={m.team_manager_import_drop_label()}
            onclick={() => fileInputEl?.click()}
            onkeydown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), fileInputEl?.click())}
            ondragover={(e) => { e.preventDefault(); isDragging = true; }}
            ondragleave={() => (isDragging = false)}
            ondrop={handleDrop}>
            <i class="bi bi-filetype-csv drop-zone-icon"></i>
            <p class="mb-1 fw-semibold">{m.team_manager_import_drop_label()}</p>
            <p class="mb-0 small text-body-secondary">{m.team_manager_import_drop_hint({ max: MAX_ROWS })}</p>
            <input bind:this={fileInputEl} type="file" class="d-none" accept=".csv,text/csv" onchange={(e) => readFile(e.currentTarget.files?.[0])} />
        </div>
        <div class="d-flex align-items-center justify-content-between mt-3 small">
            <span class="text-body-secondary">{m.team_manager_import_columns_hint()}</span>
            <button type="button" class="btn btn-link btn-sm text-decoration-none" onclick={downloadTemplate}>
                <i class="bi bi-download me-1"></i>{m.team_manager_import_template()}
            </button>
        </div>
    {:else if step === "map"}
        <div class="row g-3" in:fly={{ x: 24, duration: motion(220), easing: cubicOut }}>
            <div class="col-lg-4">
                <div class="mapping card card-body">
                    <div class="small text-body-secondary mb-3"><i class="bi bi-file-earmark-text me-1"></i>{fileName} · {m.team_manager_import_row_count({ count: rows.length })}</div>

                    <label class="form-label small fw-semibold" for="csvEmailCol">{m.team_manager_email_label()} <span class="text-danger">*</span></label>
                    <select id="csvEmailCol" class="form-select form-select-sm mb-3" bind:value={emailCol}>
                        <option value="">{m.team_manager_import_choose_column()}</option>
                        {#each headers as h (h)}<option value={h}>{h}</option>{/each}
                    </select>

                    <div class="form-label small fw-semibold mb-1">{m.team_manager_table_name()}</div>
                    <div class="btn-group btn-group-sm w-100 mb-2" role="group">
                        <input type="radio" class="btn-check" id="nameModeFull" value="full" bind:group={nameMode} />
                        <label class="btn btn-outline-secondary" for="nameModeFull">{m.team_manager_import_name_full()}</label>
                        <input type="radio" class="btn-check" id="nameModeSplit" value="split" bind:group={nameMode} />
                        <label class="btn btn-outline-secondary" for="nameModeSplit">{m.team_manager_import_name_split()}</label>
                    </div>
                    {#if nameMode === "full"}
                        <select class="form-select form-select-sm mb-3" aria-label={m.team_manager_import_name_full()} bind:value={fullNameCol}>
                            <option value="">{m.team_manager_import_no_column()}</option>
                            {#each headers as h (h)}<option value={h}>{h}</option>{/each}
                        </select>
                    {:else}
                        <div class="row g-2 mb-3">
                            <div class="col-6">
                                <select class="form-select form-select-sm" aria-label={m.team_manager_first_name_label()} bind:value={firstNameCol}>
                                    <option value="">{m.team_manager_first_name_label()}…</option>
                                    {#each headers as h (h)}<option value={h}>{h}</option>{/each}
                                </select>
                            </div>
                            <div class="col-6">
                                <select class="form-select form-select-sm" aria-label={m.team_manager_last_name_label()} bind:value={lastNameCol}>
                                    <option value="">{m.team_manager_last_name_label()}…</option>
                                    {#each headers as h (h)}<option value={h}>{h}</option>{/each}
                                </select>
                            </div>
                        </div>
                    {/if}

                    <label class="form-label small fw-semibold" for="csvRoleCol">{m.team_manager_import_role_column()}</label>
                    <select id="csvRoleCol" class="form-select form-select-sm mb-3" bind:value={roleCol}>
                        <option value="">{m.team_manager_import_no_column()}</option>
                        {#each headers as h (h)}<option value={h}>{h}</option>{/each}
                    </select>

                    <label class="form-label small fw-semibold" for="csvDefaultRole">{m.team_manager_import_default_role()}</label>
                    <select id="csvDefaultRole" class="form-select form-select-sm" bind:value={defaultRole}>
                        {#each TEAM_ROLES as role (role)}<option value={role}>{roleLabel(role)}</option>{/each}
                    </select>
                    <div class="form-text">{m.team_manager_import_default_role_help()}</div>
                </div>
            </div>

            <div class="col-lg-8">
                {#if !emailCol}
                    <div class="team-empty">
                        <i class="bi bi-envelope" aria-hidden="true"></i>
                        <p class="small text-body-secondary mb-0">{m.team_manager_import_pick_email()}</p>
                    </div>
                {:else}
                    <div class="d-flex flex-wrap gap-2 align-items-center mb-2 small">
                        <span class="badge text-bg-success">{m.team_manager_import_ready_count({ count: readyRows.length })}</span>
                        {#if skippedCount}<span class="badge text-bg-secondary">{m.team_manager_import_skipped_count({ count: skippedCount })}</span>{/if}
                        {#if seatLimit != null}<span class="text-body-secondary ms-auto">{m.team_manager_seats_used({ used: users.length, limit: seatLimit })}</span>{/if}
                    </div>
                    <div class="team-table-wrap preview-wrap">
                        <table class="table table-sm align-middle mb-0 team-table">
                            <thead>
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col">{m.team_manager_table_name()}</th>
                                    <th scope="col">{m.team_manager_table_email()}</th>
                                    <th scope="col">{m.team_manager_user_role_label()}</th>
                                    <th scope="col">{m.team_manager_table_status()}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each preview as row (row.index)}
                                    <tr class={row.status === "ready" ? "" : "row-skipped"}>
                                        <td class="text-body-secondary small">{row.index}</td>
                                        <td class="text-truncate">{[row.firstName, row.lastName].filter(Boolean).join(" ") || "—"}</td>
                                        <td class="text-truncate">{row.email || "—"}</td>
                                        <td>
                                            {roleLabel(row.role)}
                                            {#if row.roleFallback}
                                                <i class="bi bi-info-circle text-warning ms-1" title={m.team_manager_import_role_fallback({ role: row.rawRole })}></i>
                                            {/if}
                                        </td>
                                        <td>
                                            <span class="import-status status-{row.status}">{statusLabel(row.status)}</span>
                                        </td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                {/if}
            </div>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-3">
            <button type="button" class="btn btn-outline-secondary btn-sm" onclick={() => (step = "file")}>{m.team_manager_import_choose_another()}</button>
            <button type="button" class="btn btn-primary btn-sm" disabled={!readyRows.length} onclick={runImport}>
                <i class="bi bi-person-plus me-1"></i>{m.team_manager_import_run({ count: readyRows.length })}
            </button>
        </div>
    {:else}
        <div class="import-progress card card-body" in:fly={{ x: 24, duration: motion(220), easing: cubicOut }}>
            {#if step === "importing"}
                <p class="fw-semibold mb-2">{m.team_manager_import_in_progress({ done: results.length, total: readyRows.length })}</p>
            {:else}
                <p class="fw-semibold mb-2">
                    <i class="bi bi-check-circle-fill text-success me-1"></i>{m.team_manager_import_summary({ added, failed: failed.length })}
                </p>
            {/if}
            <div class="progress" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100" style="height: 0.5rem;">
                <div class="progress-bar {step === 'importing' ? 'progress-bar-striped progress-bar-animated' : ''}" style="width: {progress}%"></div>
            </div>

            {#if step === "done" && failed.length}
                <div class="small fw-semibold mt-3 mb-1">{m.team_manager_import_failed_heading()}</div>
                <ul class="small mb-0 failed-list" transition:slide={{ duration: motion(200) }}>
                    {#each failed as row (row.index)}
                        <li><span class="fw-semibold">{row.email}</span> — {row.error}</li>
                    {/each}
                </ul>
            {/if}

            {#if step === "done"}
                <div class="d-flex justify-content-end mt-3">
                    <button type="button" class="btn btn-primary btn-sm" onclick={onclose}>{m.team_manager_import_finish()}</button>
                </div>
            {/if}
        </div>
    {/if}
</div>

<style>
    .drop-zone {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.25rem;
        padding: 3rem 1rem;
        border: 2px dashed var(--bs-border-color);
        border-radius: 0.5rem;
        background: var(--bs-tertiary-bg);
        text-align: center;
        cursor: pointer;
        transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;
    }

    .drop-zone:hover,
    .drop-zone:focus-visible {
        border-color: var(--bs-primary);
        outline: none;
    }

    .drop-zone.dragging {
        border-color: var(--bs-primary);
        background: var(--bs-primary-bg-subtle);
    }

    .drop-zone-icon {
        font-size: 2.25rem;
        color: var(--bs-primary);
    }

    .import-steps {
        display: flex;
        gap: 1rem;
        padding: 0;
        list-style: none;
        counter-reset: step;
        font-size: 0.8rem;
        color: var(--bs-secondary-color);
    }

    .import-steps li {
        counter-increment: step;
    }

    .import-steps li::before {
        content: counter(step);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.25rem;
        height: 1.25rem;
        margin-right: 0.35rem;
        border-radius: 50%;
        border: 1px solid var(--bs-border-color);
        font-size: 0.7rem;
    }

    .import-steps li.active {
        color: var(--bs-body-color);
        font-weight: 600;
    }

    .import-steps li.active::before {
        background: var(--bs-primary);
        border-color: var(--bs-primary);
        color: #fff;
    }

    .preview-wrap {
        max-height: 22rem;
    }

    .row-skipped td {
        color: var(--bs-secondary-color);
    }

    .import-status {
        display: inline-block;
        padding: 0.1rem 0.5rem;
        border-radius: 999px;
        font-size: 0.72rem;
        font-weight: 600;
        white-space: nowrap;
    }

    .status-ready {
        color: var(--bs-success-text-emphasis);
        background: var(--bs-success-bg-subtle);
    }

    .status-invalid,
    .status-seat {
        color: var(--bs-danger-text-emphasis);
        background: var(--bs-danger-bg-subtle);
    }

    .status-exists,
    .status-duplicate {
        color: var(--bs-secondary-text-emphasis);
        background: var(--bs-secondary-bg-subtle);
    }

    .failed-list {
        max-height: 10rem;
        overflow-y: auto;
    }
</style>
